import bridge from '@vkontakte/vk-bridge';
import { useAdaptivity, useInsets } from '@vkontakte/vk-bridge-react';
import { RouterProvider } from '@vkontakte/vk-mini-apps-router';
import { AdaptivityProvider, AppRoot, ConfigProvider } from '@vkontakte/vkui';
import { useMemo, type ReactNode } from 'react';
import { router } from './router';

import '@vkontakte/vkui/dist/vkui.css';
import '../theme/meza.css';
import '../theme/tokens.css';

/**
 * Бутстрап приложения: тема, платформа, адаптивность, безопасные отступы,
 * роутер. Всё остальное живёт внутри <App />.
 *
 * ВАЖНО: начиная с VKUI 6 библиотека сама с Bridge не разговаривает. Раньше
 * ConfigProvider подписывался на VKWebAppUpdateConfig и подхватывал тему и
 * инсеты сам; теперь эту связь надо строить руками — ровно это и делают три
 * хука ниже. Уберёте их — приложение навсегда останется светлым, а контент
 * заедет под чёлку и под системную полосу жестов.
 */
export function AppConfig({ children }: { children: ReactNode }) {
  // vkcom — десктопная вёрстка ВК. На остальных платформах пусто: VKUI
  // определит iOS или Android сам.
  const platform = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('vk_platform') === 'desktop_web' ? ('vkcom' as const) : undefined;
  }, []);

  /**
   * Мы правда внутри ВК?
   *
   * Свои кнопки (закрыть, меню) ВК рисует поверх шапки только у себя, и место
   * под них резервирует VKUI — целых 90 px справа. Снаружи, на дев-сервере или
   * по прямой ссылке, там не появляется ничего, и шапка молча теряет четверть
   * ширины: заголовок и счёт жмутся влево, а справа висит пустота. Проверено
   * в браузере — содержимое шапки занимало 316 px из 406.
   *
   * Признак — параметры запуска: ВК приписывает их к адресу всегда, на всех
   * платформах. `isWebView()` одного не хватает: в вебе ВК он false, а кнопки
   * там есть.
   */
  const inVk = useMemo(
    () => bridge.isWebView() || new URLSearchParams(window.location.search).has('vk_app_id'),
    [],
  );

  const insets = useInsets() ?? undefined;
  const adaptivity = useAdaptivity();

  return (
    <ConfigProvider
      // Схему у ВК не спрашиваем: Меза тёмная, светлого варианта у неё нет, и
      // на светлой схеме неон по бумаге не читается вовсе. Поэтому useAppearance
      // здесь больше не участвует — это осознанный отказ, а не забытый хук.
      colorScheme="dark"
      platform={platform}
      isWebView={bridge.isWebView()}
      // Справа в шапке ВК рисует свои кнопки: место под них резервирует VKUI.
      // Но только там, где эти кнопки есть, — иначе шапка теряет 90 px впустую.
      hasCustomPanelHeaderAfter={inVk}
      // Палитра Мезы. Классы описаны в theme/tokens.css, сырые значения — в meza.css.
      tokensClassNames={{ light: 'right-mesa-tokens-light', dark: 'right-mesa-tokens-dark' }}
    >
      <AdaptivityProvider {...(adaptivity ?? {})}>
        <AppRoot mode="full" safeAreaInsets={insets}>
          <RouterProvider router={router}>{children}</RouterProvider>
        </AppRoot>
      </AdaptivityProvider>
    </ConfigProvider>
  );
}
